import React from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { AdminRoute } from '@/components/AdminRoute'
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout'
import Auth from '@/features/auth/pages/AuthPage'
import { dashboardRoutes } from '@/features/welcome/routes'
import { profileRoutes } from '@/features/profile/routes'
import { adminRoutes } from '@/features/admin/routes'
import { salesOrderRoutes } from '@/features/sales-order/routes'
import { WorkflowPage } from '@/features/workflow/WorkflowPage'
import { orderConfirmationRoutes } from '@/features/order-confirmation/routes'

// Dev only: the app's design system (not part of production builds).
const DesignSystemPage = import.meta.env.DEV ? React.lazy(() => import('@/features/design-system/DesignSystemPage')) : null

function protected_(layout: React.ReactNode) {
  return <ProtectedRoute>{layout}</ProtectedRoute>
}

/**
 * Central router: composes public routes, protected layout, and feature routes.
 *
 * Built with createBrowserRouter rather than <BrowserRouter><Routes>, which are the same routes
 * either way. A data router is what makes useBlocker work, and useBlocker is how a page with
 * unsaved work gets to ask before it is navigated away from — including navigation a component
 * starts itself, which intercepting link clicks would miss.
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Public */}
      <Route path="/auth" element={<Auth />} errorElement={<RouteErrorBoundary />} />
      {DesignSystemPage && (
        <Route path="/design-system/*" element={<React.Suspense fallback={null}><DesignSystemPage /></React.Suspense>} />
      )}

      {/* Dashboard */}
      <Route
        path="/"
        element={protected_(<AuthenticatedLayout />)}
        errorElement={<RouteErrorBoundary />}
      >
        {dashboardRoutes}
      </Route>

      {/* Profile */}
      <Route
        path="/profile"
        element={protected_(<AuthenticatedLayout />)}
        errorElement={<RouteErrorBoundary />}
      >
        {profileRoutes}
      </Route>

      {/* Admin — organization + workflow management. Root/partner organizations only. */}
      <Route
        path="/admin"
        element={protected_(
          <AdminRoute>
            <AuthenticatedLayout />
          </AdminRoute>
        )}
        errorElement={<RouteErrorBoundary />}
      >
        {adminRoutes}
      </Route>

      {/* Workflow — /workflow/:workflowId/overview/[email-inbox|validation|confirmed]
                                            /settings */}
      <Route
        path="/workflow/:workflowId"
        element={protected_(<AuthenticatedLayout />)}
        errorElement={<RouteErrorBoundary />}
      >
        {/* WorkflowPage dispatches to the right layout (e.g. OrderConfirmationLayout)
            based on workflow type. The layout renders <Outlet /> for the children below. */}
        <Route element={<WorkflowPage />}>
          {orderConfirmationRoutes}
        </Route>
      </Route>

      {/* Sales order — /sales-order/:workflowId/[overview/[awaiting-validation|done]|inbox|...]
          Its own prefix rather than a branch of /workflow/:workflowId: the two products share
          segment names, so one tree would match by declaration order, not by workflow type. */}
      <Route
        path="/sales-order/:workflowId"
        element={protected_(<AuthenticatedLayout />)}
        errorElement={<RouteErrorBoundary />}
      >
        {salesOrderRoutes}
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </>,
  ),
)

export function AppRouter() {
  return <RouterProvider router={router} />
}
